/**
 * Orchestra AI Platform - Workflow Canvas Component
 * 
 * Main canvas component for visual workflow builder using React Flow
 * 
 * @fileoverview Workflow canvas with drag-and-drop, pan, zoom, and node management
 */

import { useCallback, useEffect, useRef } from 'react';
import ReactFlow, {
  Background,
  Controls,
  MiniMap,
  useNodesState,
  useEdgesState,
  addEdge,
  useReactFlow,
  Panel,
} from 'reactflow';
import 'reactflow/dist/style.css';
import PropTypes from 'prop-types';
import { useWorkflowStore } from '../../store/workflowStore';
import { validateConnection } from '../../utils/workflowUtils';
import { APINode } from './nodes/APINode';
import { InputNode } from './nodes/InputNode';
import { OutputNode } from './nodes/OutputNode';
import { supabase, supabaseAdmin } from '../../lib/supabase';
import toast from 'react-hot-toast';
import './WorkflowCanvas.css';

/**
 * @typedef {Object} WorkflowCanvasProps
 * @property {string} workflowId - Workflow ID
 * @property {boolean} [readOnly] - Whether canvas is read-only
 * @property {Function} [onSave] - Save callback
 */

const nodeTypes = {
  apiNode: APINode,
  inputNode: InputNode,
  outputNode: OutputNode,
};

/**
 * Workflow Canvas Component
 * @param {WorkflowCanvasProps} props
 */
export function WorkflowCanvas({ workflowId, readOnly = false, onSave }) {
  const reactFlowWrapper = useRef(null);
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const { project, getViewport, fitView } = useReactFlow();

  const {
    selectedNodeId,
    selectNode,
    setNodes: setStoreNodes,
    setEdges: setStoreEdges,
    setViewport,
    loadWorkflow,
  } = useWorkflowStore();

  // Sync with store
  useEffect(() => {
    setStoreNodes(nodes);
  }, [nodes, setStoreNodes]);

  useEffect(() => {
    setStoreEdges(edges);
  }, [edges, setStoreEdges]);

  /**
   * Load workflow from database
   * @param {string} id - Workflow ID
   */
  const loadWorkflowFromDB = useCallback(async (id) => {
    if (!id) {
      console.warn('[Workflow Canvas] No workflow ID provided');
      return;
    }

    try {
      console.log('[Workflow Canvas] Loading workflow:', id);
      
      // Try RPC function first (bypasses RLS completely)
      let data, error;
      const { data: rpcData, error: rpcError } = await supabase.rpc('get_developer_workflow', {
        p_workflow_id: id
      });
      
      if (rpcError || !rpcData) {
        console.warn('[Workflow Canvas] RPC function failed, trying admin client...', rpcError);
        // Fallback to admin client
        const adminResult = await supabaseAdmin
          .from('developer_workflows')
          .select('workflow_data, name, description, project_id')
          .eq('id', id)
          .single();
        
        data = adminResult.data;
        error = adminResult.error;
      } else {
        // RPC succeeded - handle JSONB response
        if (typeof rpcData === 'string') {
          data = JSON.parse(rpcData);
        } else {
          data = rpcData;
        }
        error = null;
      }

      if (error) {
        console.error('[Workflow Canvas] Admin client error:', error);
        throw error;
      }
      
      if (!data) {
        throw new Error('Workflow not found');
      }

      console.log('[Workflow Canvas] Workflow data received:', {
        hasWorkflowData: !!data.workflow_data,
        name: data.name
      });
      
      if (data && data.workflow_data) {
        let workflowData = data.workflow_data;
        
        // Ensure workflow_data is an object
        if (typeof workflowData === 'string') {
          try {
            workflowData = JSON.parse(workflowData);
          } catch (e) {
            console.error('[Workflow Canvas] Failed to parse workflow_data:', e);
            throw new Error('Invalid workflow data format');
          }
        }
        
        // Convert workflow data to React Flow format
        if (workflowData.nodes && Array.isArray(workflowData.nodes)) {
          console.log('[Workflow Canvas] Setting nodes:', workflowData.nodes.length);
          setNodes(workflowData.nodes);
        } else {
          console.warn('[Workflow Canvas] No nodes array found, using empty array');
          setNodes([]);
        }

        if (workflowData.edges && Array.isArray(workflowData.edges)) {
          console.log('[Workflow Canvas] Setting edges:', workflowData.edges.length);
          setEdges(workflowData.edges);
        } else {
          console.warn('[Workflow Canvas] No edges array found, using empty array');
          setEdges([]);
        }
        
        // Load viewport if available
        if (workflowData.viewport) {
          setViewport(workflowData.viewport);
        } else {
          // Set default viewport if not provided
          setViewport({ x: 0, y: 0, zoom: 1 });
        }
        
        // Load into store
        loadWorkflow(workflowData);
        
        console.log('[Workflow Canvas] Workflow loaded successfully:', {
          nodes: workflowData.nodes?.length || 0,
          edges: workflowData.edges?.length || 0,
          viewport: workflowData.viewport || 'default'
        });
        
        // Fit view to show all nodes after a short delay to ensure React Flow is ready
        setTimeout(() => {
          if (workflowData.nodes && workflowData.nodes.length > 0) {
            fitView({ padding: 0.2, duration: 400 });
          }
        }, 100);
        
        toast.success(`Workflow "${data.name || 'Untitled'}" loaded`);
      } else {
        console.warn('[Workflow Canvas] Workflow data is empty');
        // Initialize with empty workflow
        setNodes([]);
        setEdges([]);
        setViewport({ x: 0, y: 0, zoom: 1 });
        toast.info('Empty workflow loaded');
      }
    } catch (error) {
      console.error('[Workflow Canvas] Failed to load workflow:', error);
      toast.error('Failed to load workflow: ' + (error.message || 'Unknown error'));
      
      // Initialize with empty workflow on error
      setNodes([]);
      setEdges([]);
      setViewport({ x: 0, y: 0, zoom: 1 });
    }
  }, [setNodes, setEdges, setViewport, loadWorkflow]);

  // Load workflow on mount
  useEffect(() => {
    if (workflowId) {
      // Load from database
      loadWorkflowFromDB(workflowId);
    }
  }, [workflowId, loadWorkflowFromDB]);

  /**
   * Handle connection between nodes
   * @param {Object} connection - Connection object
   */
  const onConnect = useCallback(
    (connection) => {
      const validation = validateConnection(connection, nodes, edges);
      if (!validation.valid) {
        toast.error(validation.error);
        return;
      }

      setEdges((eds) => addEdge(connection, eds));
    },
    [nodes, edges, setEdges]
  );

  /**
   * Handle node click
   * @param {React.MouseEvent} event
   * @param {Object} node
   */
  const onNodeClick = useCallback(
    (event, node) => {
      selectNode(node.id);
    },
    [selectNode]
  );

  /**
   * Handle pane click (deselect)
   */
  const onPaneClick = useCallback(() => {
    selectNode(null);
  }, [selectNode]);

  /**
   * Handle drop event
   * @param {React.DragEvent} event
   */
  const onDrop = useCallback(
    (event) => {
      event.preventDefault();

      const reactFlowBounds = reactFlowWrapper.current.getBoundingClientRect();
      const data = event.dataTransfer.getData('application/reactflow');

      if (!data) return;

      try {
        const api = JSON.parse(data);

        const position = project({
          x: event.clientX - reactFlowBounds.left,
          y: event.clientY - reactFlowBounds.top,
        });

        const newNode = {
          id: `api-${Date.now()}`,
          type: 'apiNode',
          position,
          data: {
            ...api,
            label: api.name || api.endpoint,
            parameters: {},
            status: 'idle',
          },
        };

        setNodes((nds) => nds.concat(newNode));
        toast.success('Node added');
      } catch (error) {
        toast.error('Failed to add node');
      }
    },
    [project, setNodes]
  );

  /**
   * Handle drag over
   * @param {React.DragEvent} event
   */
  const onDragOver = useCallback((event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  /**
   * Handle viewport change
   */
  const onMove = useCallback(() => {
    const viewport = getViewport();
    setViewport(viewport);
  }, [getViewport, setViewport]);

  /**
   * Handle nodes change
   */
  const handleNodesChange = useCallback(
    (changes) => {
      onNodesChange(changes);
      // Update selected node if deleted
      changes.forEach((change) => {
        if (change.type === 'remove' && change.id === selectedNodeId) {
          selectNode(null);
        }
      });
    },
    [onNodesChange, selectedNodeId, selectNode]
  );

  return (
    <div className="workflow-canvas" ref={reactFlowWrapper} style={{ width: '100%', height: '100%', position: 'relative' }}>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={handleNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={readOnly ? undefined : onConnect}
        onNodeClick={onNodeClick}
        onPaneClick={onPaneClick}
        onDrop={readOnly ? undefined : onDrop}
        onDragOver={onDragOver}
        onMove={onMove}
        nodeTypes={nodeTypes}
        fitView
        snapToGrid
        snapGrid={[15, 15]}
        minZoom={0.5}
        maxZoom={2}
        defaultViewport={{ x: 0, y: 0, zoom: 1 }}
        deleteKeyCode={readOnly ? null : 'Delete'}
        multiSelectionKeyCode={readOnly ? null : 'Shift'}
      >
        <Background color="#e5e7eb" gap={15} />
        <Controls />
        <MiniMap
          nodeColor={(node) => {
            if (node.type === 'inputNode') return '#10b981';
            if (node.type === 'outputNode') return '#f59e0b';
            return '#3b82f6';
          }}
          maskColor="rgba(0, 0, 0, 0.1)"
        />
        {!readOnly && (
          <Panel position="top-left" className="canvas-info">
            <div className="text-sm text-gray-600">
              {nodes.length} nodes, {edges.length} connections
            </div>
          </Panel>
        )}
      </ReactFlow>
    </div>
  );
}

WorkflowCanvas.propTypes = {
  workflowId: PropTypes.string,
  readOnly: PropTypes.bool,
  onSave: PropTypes.func,
};

